import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AddressInput from "../components/AddressInput";
import Card from "../components/ui/Card";
import { api } from "../utils/api";
import { trackEvent } from "../utils/analytics";

export default function PlanningCheck() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [prefill] = useState<string>(params.get("address") || "");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerateReport = async (addressInput: { line1: string; line2?: string; city: string; postcode: string } | string) => {
    setGenerating(true);
    setError(null);
    
    try {
      let address;
      
      // Check if we received a structured address object or a text string
      if (typeof addressInput === 'string') {
        // Parse the address text into components
        // For now, we'll use a simple approach - split by commas
        const parts = addressInput.split(',').map(p => p.trim());
        
        if (parts.length >= 3) {
          // Assume: line1, city, postcode
          address = {
            line1: parts[0],
            city: parts[parts.length - 2],
            postcode: parts[parts.length - 1],
          };
        } else if (parts.length === 2) {
          // Assume: line1, postcode
          address = {
            line1: parts[0],
            city: 'Unknown',
            postcode: parts[1],
          };
        } else {
          // Single part - treat as line1
          address = {
            line1: addressInput,
            city: 'Unknown',
            postcode: 'Unknown',
          };
        }
      } else {
        // We received a structured address object - use it directly
        address = addressInput;
      }
      
      // Generate the report using the API
      trackEvent({ name: 'report_generated' });
      const response = await api.createPlanningReport(address, 'anonymous-user');
      
      // Navigate to the report page using the transaction ID
      navigate(`/report/planning/${response.transactionId}`);
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Buyer's Report for a UK Property | UKPropertyChecks</title>
        <meta
          name="description"
          content="Generate a paid Buyer's Report that flags planning, EPC, flood-risk, and tax/regulation findings for a UK property. £29 per report."
        />
        <link rel="canonical" href="https://ukpropertychecks.co.uk/planning-check" />
        <meta property="og:title" content="Buyer's Report for a UK Property" />
        <meta property="og:description" content="Check planning, EPC, flood risk, and tax/regulation flags for any UK property. £29 per report." />
        <meta property="og:url" content="https://ukpropertychecks.co.uk/planning-check" />
      </Helmet>

      <main className="bg-slate-50 min-h-screen">
        <div className="container-main section-spacing">
          <div className="max-w-2xl">
            <h1>Buyer's Report for a UK property</h1>
            <p className="mt-3 text-slate-600">
              Factual summary from public records. Not advice.
            </p>
          </div>

          <Card>
            {error && (
              <div className="error mb-4" role="alert">
                {error}
              </div>
            )}
            
            <AddressInput
              label="Property address or postcode"
              buttonText={generating ? "Generating..." : "Generate planning report"}
              helperText={
                prefill
                  ? `Current input: ${prefill}`
                  : "Enter a UK address. For example: 10 Downing Street, London, SW1A 2AA"
              }
              onSubmit={handleGenerateReport}
            />
            <div className="mt-4 text-sm text-slate-700">
              Price: <span className="font-semibold text-slate-950">£29</span> per report (one-off). Payment required before viewing full report.
            </div>
          </Card>

          <Card className="mt-8">
            <h2>What the report includes</h2>
            <ul className="mt-4 list-disc pl-5 text-sm text-slate-800 space-y-2">
              <li>Report summary with constraint and designation counts at a glance</li>
              <li>Planning constraints: Green Belt, AONB, National Park, flood zone (with specific zone level), TPO, SSSI</li>
              <li>Heritage designations from Historic England (listed buildings, scheduled monuments, and more)</li>
              <li>Conservation area status</li>
              <li>Article 4 Direction status (where recorded)</li>
              <li>Energy Performance Certificate (EPC) rating and details (where available)</li>
              <li>What each constraint means for your property</li>
              <li>Local planning authority details</li>
              <li>Downloadable PDF report</li>
            </ul>
            <p className="mt-4 text-sm text-slate-600">
              Data sourced from planning.data.gov.uk, Historic England, the EPC register, and Postcodes.io. Coverage varies by area and dataset. This report provides factual information only. It does not provide risk assessment, legal advice, or recommendations.
            </p>
          </Card>
        </div>
      </main>
    </>
  );
}
